/**
 * Sticky Header Component
 *
 * Applies a "scrolled" state to the header when the page is scrolled down:
 * - Adds data-scrolled attribute for CSS styling
 * - Triggers at a configurable scroll threshold (default: 50px)
 *
 * Works seamlessly with Lenis smooth scroll.
 */

/**
 * Initialize sticky header scroll detection
 * Finds the header element and monitors scroll position
 *
 * @param {Object} options - Configuration options
 * @param {number} options.threshold - Scroll distance in pixels before applying scrolled state (default: 50)
 */
export function initStickyHeader(options = {}) {
  const { threshold = 50 } = options;

  const header = document.querySelector("header");

  if (!header) {
    return; // No header element found
  }

  let isScrolled = false;

  /**
   * Handle scroll events
   * Updates the header's data-scrolled attribute based on scroll position
   */
  function handleScroll() {
    const scrollY = window.scrollY || window.pageYOffset;

    if (scrollY > threshold && !isScrolled) {
      // Scrolled past threshold - add scrolled state
      isScrolled = true;
      header.setAttribute("data-scrolled", "true");
    } else if (scrollY <= threshold && isScrolled) {
      // Back to top - remove scrolled state
      isScrolled = false;
      header.setAttribute("data-scrolled", "false");
    }
  }

  // Initialize with current scroll position
  handleScroll();

  // Listen for scroll events
  // Note: If using Lenis, this will work with smooth scroll
  window.addEventListener("scroll", handleScroll, { passive: true });

  // Also listen on the window for Lenis compatibility
  if (window.lenis) {
    window.lenis.on("scroll", handleScroll);
  }
}

/**
 * Cleanup function to remove scroll listeners
 * Useful for SPA navigation or dynamic content updates
 */
export function destroyStickyHeader() {
  // Remove scroll listeners if needed
  // (Implementation depends on whether you need dynamic cleanup)
}

export const stickyHeader = {
  init: initStickyHeader,
  destroy: destroyStickyHeader,
};
